import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { showErrorMessage } from '@actions/messageconfirmation';

import { Button } from '@commons/utils/styledLibraryComponents';
import { getFormattedLastUpdatedText } from '@commons/FullScreenModal/utils';
import { LoadingIcon } from '@commons/Metrics/components/Stats/styledComponents';
import AuditHistoryDrawer from '@commons/HistoryDrawer/AuditHistoryDrawer';
import SpinnerIcon from '@assets/icons/new-loader.svg';
import Stepper from '@commons/Stepper';

import useAuditHistory from '@hooks/useAuditHistory';

import theme from '@theme';

import { EXTERNAL_ORDER_STATUS, getExternalOrderFormSteps } from '../../constants';

import {
  LastUpdate,
  OrderInfos,
  Reference,
  TopBar,
  TopBarButtons,
  TopBarRight,
} from './styledComponents';

/**
 * Background of the active step chip, tinted as the status badge of the list from `En préparation`
 * onwards. Both prepared statuses share the same step and label, so this tint is the only place the
 * form spells the compliance out. The earlier statuses keep the default grey of the stepper.
 */
const ACTIVE_STEP_BACKGROUND_BY_STATUS = {
  [EXTERNAL_ORDER_STATUS.IN_PREPARATION]: theme.colors.infoLightPurple,
  [EXTERNAL_ORDER_STATUS.PREPARED_COMPLIANT]: theme.colors.brand.primaryLight,
  [EXTERNAL_ORDER_STATUS.PREPARED_NON_COMPLIANT]: theme.colors.infoLightOrange,
};

const AUDIT_BUTTON_CUSTOM_STYLE = {
  paddingRight: '0px',
  height: '20px',
  font: theme.fonts.textSmallHeight14,
};

/**
 * Header shared by the order views: the status flow, the reference of the order and its last update,
 * then the buttons its status allows — passed as children, they are the only part that changes from
 * one status to the next.
 *
 * The last update is only shown once the order exists server-side: an order being created has none.
 * It opens the history of the order at the click (COR-1726) as soon as that history holds an event;
 * an order older than the audit logs falls back on the plain, unclickable text.
 */
const OrderTopBar = (props) => {
  const { status, reference, updatedAt, isSaved, externalOrderId, children, displayErrorMessage } =
    props;

  const steps = useMemo(() => getExternalOrderFormSteps(), []);

  // Both prepared statuses share the same step: the compliance is not spelled out in the flow.
  const activeStepKey = useMemo(
    () => steps.find(({ statuses }) => statuses.includes(status))?.key ?? null,
    [steps, status],
  );

  const {
    auditLogs,
    auditButtonLabel,
    isAuditDrawerOpened,
    fetchAuditHistory,
    toggleAuditDrawer,
    closeAuditDrawer,
  } = useAuditHistory({ showErrorMessage: displayErrorMessage, targetId: externalOrderId });

  const [isAuditHistoryFetched, setIsAuditHistoryFetched] = useState(false);

  useEffect(() => {
    if (!externalOrderId) {
      return;
    }

    const fetchExternalOrderHistory = async () => {
      await fetchAuditHistory(externalOrderId);

      setIsAuditHistoryFetched(true);
    };

    fetchExternalOrderHistory();
  }, [externalOrderId, fetchAuditHistory]);

  return (
    <>
      <TopBar>
        <Stepper
          activeBackgroundColor={ACTIVE_STEP_BACKGROUND_BY_STATUS[status]}
          activeStepKey={activeStepKey}
          steps={steps}
        />
        <TopBarRight>
          <OrderInfos>
            <Reference>{reference}</Reference>
            {isSaved && (
              <>
                {!isAuditHistoryFetched && (
                  <LoadingIcon
                    alt={'component-loader'}
                    height="16px"
                    src={SpinnerIcon}
                    width="16px"
                  />
                )}
                {isAuditHistoryFetched && !auditButtonLabel && (
                  <LastUpdate>{getFormattedLastUpdatedText(updatedAt)}</LastUpdate>
                )}
                {isAuditHistoryFetched && !!auditButtonLabel && (
                  <Button
                    buttonCustomStyle={AUDIT_BUTTON_CUSTOM_STYLE}
                    color={'inpulse-outline'}
                    fontColor={theme.colors.blacks.ipBlack4}
                    handleClick={toggleAuditDrawer}
                    icon={'/images/inpulse/history.svg'}
                    iconOnLeft={false}
                    label={auditButtonLabel}
                    noBorder
                  />
                )}
              </>
            )}
          </OrderInfos>
          <TopBarButtons>{children}</TopBarButtons>
        </TopBarRight>
      </TopBar>

      <AuditHistoryDrawer
        auditLogs={auditLogs}
        isOpened={isAuditDrawerOpened}
        subtitle={reference}
        onClose={closeAuditDrawer}
      />
    </>
  );
};

OrderTopBar.propTypes = {
  status: PropTypes.string.isRequired,
  reference: PropTypes.string,
  updatedAt: PropTypes.string,
  isSaved: PropTypes.bool.isRequired,
  externalOrderId: PropTypes.string,
  children: PropTypes.node,
  displayErrorMessage: PropTypes.func.isRequired,
};

OrderTopBar.defaultProps = {
  reference: '',
  updatedAt: null,
  externalOrderId: null,
  children: null,
};

const mapDispatchToProps = (dispatch) => ({
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(null, mapDispatchToProps)(OrderTopBar);
